import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { ScrollArea } from './ui/scroll-area';
import { CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import { NoData } from './ui/noData';
import { Tooltip, useMediaQuery } from '@mui/material';
import { DarkTooltip } from './ui/darkTooltip';
import { InfoOutlined } from '@mui/icons-material';
import { usePdfMode, usePdfVariant } from './pdf/PdfModeContext';

interface SentimentAnalysisProps {
  platform: string;
  sentimentData?:any;
  loading?:boolean;
}

const sentimentDataHardCoded = [
  { name: 'Positive', value: 68.0, count: 68, color: '#22c55e' },
  { name: 'Neutral', value: 24.0, count: 24, color: '#f59e0b' },
  { name: 'Negative', value: 8.0, count: 8, color: '#ef4444' }
];


const colors = [
  {color: '#22c55e'},
  {color: '#f59e0b'},
  {color: '#ef4444'}
]
const SENTIMENT_COLORS: Record<string, string> = {
  positive: "#22c55e",
  neutral: "#f59e0b",
  negative: "#ef4444"
};



const comments = [
  {
    id: 1,
    comment: 'Love this product! Best purchase ever!',
    sentiment: 'positive'
  },
  {
    id: 2,
    comment: 'Amazing quality and fast delivery!',
    sentiment: 'positive'
  },
  {
    id: 3,
    comment: 'Highly recommend! Exceeded expectations!',
    sentiment: 'positive'
  },
  {
    id: 4,
    comment: 'Good product, but delivery was a bit slow.',
    sentiment: 'neutral'
  },
  {
    id: 5,
    comment: 'Exactly what I was looking for. Very satisfied!',
    sentiment: 'positive'
  },
  {
    id: 6,
    comment: 'The quality is decent for the price.',
    sentiment: 'neutral'
  },
  {
    id: 7,
    comment: 'Great results! Will definitely buy again.',
    sentiment: 'positive'
  },
  {
    id: 8,
    comment: 'Not bad, but I expected more features.',
    sentiment: 'neutral'
  }
];

const getSentimentBg = (sentiment: string) => {
  switch (sentiment) {
    case 'positive':
      return 'bg-green-50';
    case 'negative':
      return 'bg-red-50';
    default:
      return 'bg-yellow-50';
  }
};

const getSentimentIcon = (sentiment: string) => {
  switch (sentiment) {
    case 'positive':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case 'negative':
      return <XCircle className="h-4 w-4 text-red-600" />;
    default:
      return <AlertCircle className="h-4 w-4 text-yellow-600" />;
  }
};

const getSentimentText = (sentiment: string) => {
  switch (sentiment) {
    case 'positive':
      return 'text-green-700';
    case 'negative':
      return 'text-red-700';
    default:
      return 'text-yellow-700';
  }
};


export function SentimentAnalysis({ platform, sentimentData }: SentimentAnalysisProps) {
  // PDF: labels always shown, comments printed instead of a fixed-height scroll box
  const pdfMode = usePdfMode();
  const pdfVariant = usePdfVariant();
  const PDF_COMMENTS = 6; // keeps the sentiment + hashtag row on one PDF page
  const [selectedSentiment, setSelectedSentiment] = useState<string | null>(null);
// const overallScore = 80.0;
const weights: any = {
  positive: 1.0,
  neutral: 0.5,
  negative: 0, // or -0.5
};



const realComments = sentimentData?.comments ?? [];

const filteredComments = selectedSentiment
  ? realComments.filter((c: any) => c.sentiment === selectedSentiment.toLowerCase())
  : realComments;


const normalizedChartData = Array.isArray(sentimentData?.chart_data)
  ? sentimentData.chart_data
  : [];

const hasRealSentiment = normalizedChartData.length > 0;

const isAllNullValues =
  normalizedChartData.length > 0 &&
  normalizedChartData.every((item: any) => item.value === null);

const noSentiment = !hasRealSentiment || isAllNullValues;

const pieData = !noSentiment
  ? normalizedChartData.map((item: any) => ({
      ...item,
      value: item.value ?? 0,                   // still render safely
      color: SENTIMENT_COLORS[item.name.toLowerCase()],
    }))
  : [];
  const overallScore = (() => {
  if (!pieData || pieData.length === 0) return 0;

  return pieData.reduce((acc: number, item: any) => {
    const key = item.name.toLowerCase();
    const weight = weights[key] ?? 0;
    return acc + item.value * weight;
  }, 0);
})();
const isLg = useMediaQuery("(min-width:1300px)");
const isMd = useMediaQuery("(min-width:1025px)");
const isSm = useMediaQuery("(min-width:576px)");

// the media queries follow the browser window, not the PDF width -- fixed sizes there
const radius = pdfVariant === 'mobile' ? 70 : pdfMode ? 90 : isLg ? 90 : isMd ? 70 : isSm ? 90 : 70;
const innerRadius = pdfVariant === 'mobile' ? 52 : pdfMode ? 70 : isLg ? 70 : isMd ? 50 : isSm ? 70 : 50;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Overall Sentiment Analysis
        
         

        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 justify-content-center">
          {/* Left side - Chart and breakdown */}
          {noSentiment ? (
            <div className="col-span-2 flex justify-center items-center py-10">
              <NoData message="No sentiment data available for the selected range." />
            </div>
          ) : (
            <>
                  <div className="space-y-1 w-60">
                    <div className="relative">
                      {}
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            isAnimationActive={!pdfMode}
                          
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={innerRadius}
                            outerRadius={radius}
                            fill="#8884d8"
                            dataKey="value"
                            startAngle={90}
                            endAngle={450}
                          >
                            {pieData.map((entry: any, index: number) => (
                              <Cell key={index} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute top-1/2 left-1/2 text-center" style={{display:'flex', flexDirection:'column', transform:'translate(-50%,-50%)'}}>
                        <p className="text-xs text-slate-500 mb-1">Overall Score

                          {!pdfMode && (
                          <DarkTooltip title={platform === 'all' ? "Weighted sentiment score for all comments during the selected timeframe. Uses: Positive (1.0), Neutral (0.5), Negative (0.0) to show overall audience mood." : "Weighted sentiment score for all comments during the selected timeframe. Uses: Positive (1.0), Neutral (0.5), Negative (0.0) to show overall audience mood." }>
                            <InfoOutlined  style={{marginLeft:'5px', fontSize:'18px'}} />
                          </DarkTooltip>
                          )}
                        </p>
                        <p className="text-slate-900">{overallScore.toFixed(2)}%</p>
                      </div>
                    </div>

                    
                  </div>
                 <div className="space-y-1 w-auto align-self-center">
                      {pieData?.map((item:any, index:any) => (
                        <div 
                          key={index} 
                          className={`flex items-center justify-between p-3 px-4 gap-3 rounded-lg cursor-pointer transition-colors ${
                            selectedSentiment === item.name 
                              ? 'bg-slate-100 shadow-sm' 
                              : 'hover:bg-slate-50'
                          }`}
                          onClick={() => setSelectedSentiment(selectedSentiment === item.name ? null : item.name)}
                        >
                          <div className="flex items-center gap-0">
                            <div 
                              className="w-3 h-3 rounded-full me-2" 
                              style={{ backgroundColor: item.color }}
                            />
                            <span className={`text-sm ${pdfMode ? 'inline' : 'hidden lgcustom:inline'} text-slate-700`}>
                              {item?.name
                                ? item.name.charAt(0).toUpperCase() + item.name.slice(1)
                                : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-900" style={{fontSize:'13px'}}>{item.value}%</span>
                            {/* <span className="text-sm text-slate-500">({item.count})</span> */}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
        </div>
               <div className="space-y-3">
                    <p className="text-sm text-slate-700">
                      {selectedSentiment ? `${selectedSentiment
                                ? selectedSentiment.charAt(0).toUpperCase() + selectedSentiment.slice(1)
                                : ''} Comments` : 'All Comments'}
                      {pdfMode && filteredComments?.length > PDF_COMMENTS && (
                        <span className="text-slate-500"> (showing {PDF_COMMENTS} of {filteredComments.length})</span>
                      )}
                    </p>
                    <ScrollArea className={`${pdfMode ? '' : platform === 'facebook' ? "h-[300px]" : "h-[300px]"}  min-w-0`}>
                      <div className="space-y-2">
                        {filteredComments && filteredComments.length > 0 && (pdfMode ? filteredComments.slice(0, PDF_COMMENTS) : filteredComments)?.map((comment:any, index:number) => (
                          <div 
                            key={index} 
                            data-pdf-row
                            className={`${pdfMode ? 'px-3 py-2' : 'p-3'} rounded-lg ${getSentimentBg(comment.sentiment)}`}
                          >
                            <div className="flex items-start gap-2">
                              {getSentimentIcon(comment.sentiment)}
                              <span className="text-sm text-slate-700">
                              {comment?.name
                                ? comment.name.charAt(0).toUpperCase() + comment.name.slice(1)
                                : ''}
                            </span>
                              {/* PDF: sentiment label on the same line as the comment, to keep this card short */}
                              <div className={`flex-1 break-all ${pdfMode ? 'flex items-baseline gap-2' : ''}`}>
                                <p className={`text-xs capitalize ${pdfMode ? 'mb-0 shrink-0' : 'mb-1'} ${getSentimentText(comment.sentiment)}`}>
                                  {comment.sentiment}
                                </p>
                                <p className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.comment) }}></p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
      </CardContent>
    </Card>
  );
}
